/**
 * Parses a timestamp coming from the API. The backend emits naive UTC strings
 * (e.g. "2026-06-03T05:39:00" with no "Z"), which the browser would otherwise
 * read as LOCAL time : making everything look offset by the local UTC offset
 * (e.g. "2h ago" instead of "just now" in GMT+2). When the string carries no
 * timezone designator we append "Z" so it is parsed as UTC; the browser then
 * renders it in the local zone automatically. Strings that already have a
 * timezone (Z or ±hh:mm) are left untouched, so this is forward-compatible.
 */
export const parseApiDate = (value?: string | null): Date | null => {
	if (!value) return null;
	const s = String(value).trim();
	if (!s) return null;
	const hasTime = s.includes("T") || /\d{2}:\d{2}/.test(s);
	const hasTz = /[zZ]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s);
	const date = new Date(hasTime && !hasTz ? `${s}Z` : s);
	return Number.isNaN(date.getTime()) ? null : date;
};

/** Formats an ISO date string into a localized medium-date/short-time string, returning "Unknown" if invalid. */
const formatExactDate = (value?: string) => {
	const date = parseApiDate(value);
	if (!date) return "Unknown";
	return date.toLocaleString(undefined, {
		dateStyle: "medium",
		timeStyle: "short"
	});
};

/** Formats an ISO date string as a human-readable relative time (e.g. "5m ago", "2d ago"). */
const formatRelativeTime = (value?: string | null) => {
	const date = parseApiDate(value);
	if (!date) return "-";
	const diffMs = Date.now() - date.getTime();
	const minutes = Math.round(diffMs / 60000);
	if (Math.abs(minutes) < 1) return "just now";
	if (Math.abs(minutes) < 60) {
		return minutes > 0 ? `${minutes}m ago` : `in ${Math.abs(minutes)}m`;
	}
	const hours = Math.round(minutes / 60);
	if (Math.abs(hours) < 24) {
		return hours > 0 ? `${hours}h ago` : `in ${Math.abs(hours)}h`;
	}
	const days = Math.round(hours / 24);
	return days > 0 ? `${days}d ago` : `in ${Math.abs(days)}d`;
};

/** Formats an ISO date string as a French relative time (« à l'instant », « il y a 5 min »,
 *  « il y a 3 j »…). Same parsing rules as {@link formatRelativeTime}, French wording. */
const formatRelativeTimeFr = (value?: string | null) => {
	const date = parseApiDate(value);
	if (!date) return "-";
	const diffMs = Date.now() - date.getTime();
	const future = diffMs < 0;
	const wrap = (label: string) => (future ? `dans ${label}` : `il y a ${label}`);
	const minutes = Math.abs(Math.round(diffMs / 60000));
	if (minutes < 1) return "à l'instant";
	if (minutes < 60) return wrap(`${minutes} min`);
	const hours = Math.round(minutes / 60);
	if (hours < 24) return wrap(`${hours} h`);
	const days = Math.round(hours / 24);
	if (days < 31) return wrap(`${days} j`);
	const months = Math.round(days / 30.44);
	if (months < 12) return wrap(`${months} mois`);
	const years = Math.round(days / 365.25);
	return wrap(`${years} an${years > 1 ? "s" : ""}`);
};

/** Full French date + time (« 16 juillet 2026 à 14:05 ») : used as the exact-value tooltip
 *  behind relative times. Empty string when the value is missing/invalid. */
const formatExactDateFr = (value?: string | null) => {
	const date = parseApiDate(value);
	if (!date) return "";
	return date.toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" });
};

export { formatExactDate, formatRelativeTime, formatRelativeTimeFr, formatExactDateFr };
